// Core packages
import {
  Component, Inject,
  OnDestroy, OnInit
} from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

// Custom packages
import { PlatformNotesService } from 'src/app/components/platform-note-card/platformNotes.service';
import { AuthService } from "src/app/modules/auth/auth.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { User } from "src/app/shared/models/user/user.model";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

@Component({
  selector: "app-associate-balances-platform-note-dialog",
  templateUrl: "./associate-balances-platform-note-dialog.component.html",
  styleUrls: ["./associate-balances-platform-note-dialog.component.scss"]
})
export class AssociateBalancesPlatformNoteDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  user: User = new User();
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.confirm"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  // END - Add subUser form

  dialogIsLoading = true;

  balances: any[] = [];
  selectAll = false;

  filterProperty = "";

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<AssociateBalancesPlatformNoteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public configService: ConfigService,
    private toastrService: ToastrService,
    private cookieService: CookieService,
    private translateService: TranslateService,
    private helperService: HelperService,
    private authService: AuthService,
    private apiService: ApiService,
    private platformNotesService: PlatformNotesService,
    private propertiesService: PropertiesService,
  ) {
    dialogRef.disableClose = true;
  }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    const open = this.translateService.instant(
      "balances.status.open"
    );
    const closed = this.translateService.instant(
      "balances.status.closed"
    );
    const pending = this.translateService.instant(
      "balances.status.pending"
    ); 

    let propertyBalances: any[] = [];
    if (
      this.data.propertyBalances &&
      Array.isArray(this.data.propertyBalances)
    ) {
      propertyBalances = this.data.propertyBalances;
    }

    // View data
    if (this.data.propertyBalances && Array.isArray(this.data.propertyBalances)) {
      for (const item of this.data.propertyBalances) {
        if (item.status !== 'closed') {
          this.balances.push({
            id: item._id,
            number: item.number,
            status: item.status === 'open' ? open : pending,
            value: this.data.balances && this.data.balances.includes(item._id.toString()) ? true : false,
            visible: true,
          });
        }
      }
    }

    // Stop loading dialog
    this.dialogIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(data?: any): void {
    this.dialogRef.close(data);
  }
  
  /**
   * Handle select all properties
   *
   * @since 1.0.0
   */
  onSelectAll(checked: boolean): void {
    this.selectAll = checked;
    if (!checked) {
      this.balances.forEach((balance: any) => {
        balance["value"] = false;
      });
    } else if (checked) {
      this.balances.forEach((balance: any) => {
        balance["value"] = true;
      });
    }
  }
  
  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {
    const balancesSelected: string[] = [];
    if (this.balances && Array.isArray(this.balances)) {
      this.balances.forEach((item: any) => {
        if (item.value) {
          balancesSelected.push(item.id);
        }
      })
    }

    const data = {
      balances: balancesSelected,
    }
    this.subscriptions.push(
      this.platformNotesService
        .updateNote(this.data.noteId, data)
        .pipe(finalize(() => (this.formBtnOptions.active = false)))
        .subscribe(
          res => {
            if (res.status) {
              this.toastrService.success(res.message);
              this.onClose({ status: true });
              return;
            }
            this.toastrService.error(
              this.translateService.instant(
                "common.errors.unknown_server_error"
              )
            );
          },
          err => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }
  
  /**
   * Handle properties filtering
   *
   * @since 1.0.0
   */
  onFilterProperties(): void {
    if (this.filterProperty !== '') {
      this.selectAll = false;
    }
    const regex = new RegExp(this.filterProperty.trim().toLowerCase(), "gim");
    this.balances.forEach((el: any) => {
      if (
        (el.number && el.number.search(regex) > -1)
      ) {
        el.visible = true;
      } else {
        el.visible = false;
        el.value = false;
      }
    });
  }
}
